import { createAction } from '@reduxjs/toolkit';

import { type AssetFileKind, type Board, type Color, type Library } from '@air/api/types';

import { type CustomFieldValueFilterBase } from '~/components/Filters/CustomFieldFilter/utils';
import { type ItemTypeFilter } from '~/components/Filters/types';
import { type CustomFieldFilterType, type FilterType } from '~/components/FiltersMenu/types';
import { type SmartTagBase } from '~/components/SmartTagSelect/utils';
import { type TagBase } from '~/components/Tags/utils';
import {
  type DateCustomFieldFilter,
  type DateRangeType,
  type FilterLogicType,
  type Filters,
  type OtherFiltersType,
  type PlainTextCustomFieldFilter,
  type SelectedFilter,
  type StringArrayFilterType,
  type UploaderFilterType,
} from '~/store/filters/types';
import { type PersonWithDefaultFace } from '~/swr-hooks/person/types';

export const addFilterAction = createAction<{ filter: SelectedFilter }>('ADD_FILTER');
export const removeFiltersWithoutSelectionAction = createAction('REMOVE_FILTERS_WITHOUT_SELECTION');
export const removeFilterAction = createAction<{ filter: SelectedFilter }>('REMOVE_FILTER');
export const changeFilterTypeAction = createAction<{ oldFilter: SelectedFilter; newFilter: SelectedFilter }>(
  'CHANGE_FILTER_TYPE',
);
export const setSelectedFiltersAction = createAction<{ filters: SelectedFilter[] }>('SET_SELECTED_FILTERS');
export const removeAllFiltersAction = createAction('REMOVE_ALL_FILTERS');
export const removeFiltersAction = createAction<{ filters: FilterType[]; customFields: CustomFieldFilterType[] }>(
  'REMOVE_FILTERS',
);

export const setFiltersValuesAction = createAction<{ filters: Partial<Filters> }>('SET_FILTERS_VALUES_ACTION');

export const addItemTypeFilterAction = createAction<{ type: ItemTypeFilter }>('ADD_ITEM_TYPE_FILTER');
export const removeItemTypeFilterAction = createAction<{ type: ItemTypeFilter }>('REMOVE_ITEM_TYPE_FILTER');
export const clearItemTypeFiltersAction = createAction('CLEAR_ITEM_TYPE_FILTER');

export const addColorFilterValueAction = createAction<{ color: Color['hex'] }>('ADD_COLOR_FILTER');
export const removeColorFilterValueAction = createAction<{ color: Color['hex'] }>('REMOVE_COLOR_FILTER');
export const clearColorFilterValuesAction = createAction('CLEAR_COLOR_FILTER');
export const addColorRangeFilterValueAction = createAction<number>('ADD_COLOR_RANGE_FILTER');

export const setTagsFilterLogicAction = createAction<{ logic: FilterLogicType }>('SET_TAGS_FILTER_LOGIC');
export const setTagsFiltersValueAction = createAction<{ tags: TagBase[] }>('SET_TAGS_FILTERS');
export const clearTagsFilterValuesAction = createAction('CLEAR_TAGS_FILTER');

export const setSmartTagsFilterLogicAction = createAction<{ logic: FilterLogicType }>('SET_SMART_TAGS_FILTER_LOGIC');
export const setSmartTagsFiltersValueAction = createAction<{ smartTags: SmartTagBase[] }>('SET_SMART_TAGS_FILTERS');
export const clearSmartTagsFilterValuesAction = createAction('CLEAR_SMART_TAGS_FILTER');

export const setSimilarToImageFilterValueAction = createAction<{ similarToImage: string }>(
  'SET_SIMILAR_TO_IMAGE_FILTER',
);
export const clearSimilarToImageFilterValueAction = createAction('CLEAR_SIMILAR_TO_IMAGE_FILTER');

export const setSimilarToFilterAction = createAction<{ similarTo: string }>('SET_SIMILAR_TO_FILTER');
export const clearSimilarToFilterAction = createAction('CLEAR_SIMILAR_TO_FILTER');

export const addImportedKeywordsFilterValueAction = createAction<{ keyword: string }>('ADD_IMPORTED_KEYWORDS_FILTER');
export const removeImportedKeywordsFilterValueAction = createAction<{ keyword: string }>(
  'REMOVE_IMPORTED_KEYWORDS_FILTER',
);
export const setImportedKeywordsFilterLogicAction = createAction<{ logic: FilterLogicType }>(
  'SET_IMPORTED_KEYWORDS_LOGIC',
);
export const setImportedKeywordsFiltersValueAction = createAction<{ keywords: string[] }>(
  'SET_IMPORTED_KEYWORDS_FILTERS',
);

export const clearImportedKeywordsFilterValuesAction = createAction('CLEAR_IMPORTED_KEYWORDS_FILTER');

export const setUploadersFilterValuesAction = createAction<{ uploaders: UploaderFilterType[] }>(
  'SET_UPLOADERS_FILTERS',
);
export const clearUploadersFilterValuesAction = createAction('CLEAR_UPLOADERS_FILTER');

export const setCameraMakeFilterValuesAction = createAction<{ make: string }>('SET_CAMERA_MAKE_FILTERS');
export const setCameraModelsFilterValuesAction = createAction<{ models: string[] }>('SET_CAMERA_MODELS_FILTERS');
export const clearCamerasFilterValuesAction = createAction('CLEAR_CAMERAS_FILTER');

export const addOtherFilterValueAction = createAction<{ filter: OtherFiltersType }>('ADD_OTHERS_FILTER');
export const removeOtherFilterValueAction = createAction<{ filter: OtherFiltersType }>('REMOVE_OTHERS_FILTER');
export const setOtherFiltersValuesAction = createAction<{ filters: OtherFiltersType[] }>('SET_OTHERS_FILTERS');
export const clearOtherFiltersValuesAction = createAction('CLEAR_OTHERS_FILTER');

export const setCustomFieldFilterValuesAction = createAction<{
  customFieldId: CustomFieldFilterType['id'];
  values: CustomFieldValueFilterBase[];
}>('ADD_CUSTOM_FIELD_FILTER_VALUES');
export const setCustomFieldFilterLogicAction = createAction<{
  customFieldId: CustomFieldFilterType['id'];
  logic: FilterLogicType;
}>('SET_CUSTOM_FIELD_FILTER_LOGIC_TYPE');
export const clearCustomFieldFilterValuesAction = createAction<{ customFieldId: CustomFieldFilterType['id'] }>(
  'CLEAR_CUSTOM_FIELD_FILTER_VALUES_ACTION',
);
export const setPlainTextCustomFieldFilterAction = createAction<{
  customFieldId: CustomFieldFilterType['id'];
  plainText?: PlainTextCustomFieldFilter;
}>('SET_PLAIN_TEXT_CUSTOM_FIELD_FILTER');
export const setDateCustomFieldFilterAction = createAction<{
  customFieldId: CustomFieldFilterType['id'];
  date: DateCustomFieldFilter;
}>('SET_DATE_CUSTOM_FIELD_FILTER');

export const setCreatedDateFilterTypeAction = createAction<{ type: DateRangeType }>('SET_CREATED_DATE_FILTER_TYPE');
export const setCreatedDateFilterStartDateAction = createAction<{ startDate?: Date | null }>(
  'SET_CREATED_DATE_FILTER_START_DATE',
);
export const clearCreatedDateFilterValueAction = createAction('CLEAR_CREATED_DATE_FILTER_VALUE');
export const setCreatedDateFilterEndDateAction = createAction<{ endDate?: Date | null }>(
  'SET_CREATED_DATE_FILTER_END_DATE',
);

export const setModifiedDateFilterTypeAction = createAction<{ type: DateRangeType }>('SET_MODIFIED_DATE_FILTER_TYPE');
export const setModifiedDateFilterStartDateAction = createAction<{ startDate?: Date | null }>(
  'SET_MODIFIED_DATE_FILTER_START_DATE',
);
export const clearModifiedDateFilterValueAction = createAction('CLEAR_MODIFIED_DATE_FILTER_VALUE');
export const setModifiedDateFilterEndDateAction = createAction<{ endDate?: Date | null }>(
  'SET_MODIFIED_DATE_FILTER_END_DATE',
);

export const setUploadedDateFilterTypeAction = createAction<{ type: DateRangeType }>('SET_UPLOADED_DATE_FILTER_TYPE');
export const setUploadedDateFilterStartDateAction = createAction<{ startDate?: Date | null }>(
  'SET_UPLOADED_DATE_FILTER_START_DATE',
);
export const clearUploadedDateFilterValueAction = createAction('CLEAR_UPLOADED_DATE_FILTER_VALUE');
export const setUploadedDateFilterEndDateAction = createAction<{ endDate?: Date | null }>(
  'SET_UPLOADED_DATE_FILTER_END_DATE',
);

export const setStringArrayFilterValuesAction = createAction<{ filter: StringArrayFilterType; values: string[] }>(
  'SET_STRING_ARRAY_FILTER_VALUES',
);
export const clearStringArrayFilterValuesAction = createAction<{ filter: StringArrayFilterType }>(
  'CLEAR_STRING_ARRAY_FILTER_VALUES',
);

export const setPeopleFilterLogicAction = createAction<{ logic: FilterLogicType }>('SET_PEOPLE_FILTER_LOGIC');
export const setPeopleFiltersValueAction = createAction<{ people: PersonWithDefaultFace[] }>('SET_PEOPLE_FILTERS');
export const clearPeopleFilterValuesAction = createAction('CLEAR_PEOPLE_FILTER');

export const setTakenDateFilterTypeAction = createAction<{ type: DateRangeType }>('SET_TAKEN_DATE_FILTER_TYPE');
export const setTakenDateFilterStartDateAction = createAction<{ startDate?: Date | null }>(
  'SET_TAKEN_DATE_FILTER_START_DATE',
);
export const clearTakenDateFilterValueAction = createAction('CLEAR_TAKEN_DATE_FILTER_VALUE');
export const setTakenDateFilterEndDateAction = createAction<{ endDate?: Date | null }>(
  'SET_TAKEN_DATE_FILTER_END_DATE',
);

export const setLocationsBoardsAction = createAction<{ boards: Board['id'][] }>('SET_LOCATIONS_BOARDS');
export const toggleLocationsBoardAction = createAction<{ board: Board['id'] }>('TOGGLE_LOCATIONS_BOARD');
export const setLocationsLibrariesAction = createAction<{ libraries: Library['id'][] }>('SET_LOCATIONS_LIBRARIES');
export const toggleLocationsLibraryAction = createAction<{ library: Library['id'] }>('TOGGLE_LOCATIONS_LIBRARY');
export const clearLocationsFilterValuesAction = createAction('CLEAR_LOCATIONS_FILTER');

export const setAssetFileKindFilterValuesAction = createAction<{
  assetFileKind: AssetFileKind[];
}>('SET_ASSET_FILE_KIND_FILTER_VALUES');
export const addAssetFileKindFilterValueAction = createAction<{
  assetFileKind: AssetFileKind;
}>('ADD_ASSET_FILE_KIND_FILTER_VALUE');
export const addMultipleAssetFileKindFilterValuesAction = createAction<{ assetFileKinds: AssetFileKind[] }>(
  'ADD_MULTIPLE_ASSET_FILE_KIND_FILTER_VALUES',
);
export const removeMultipleAssetFileKindFilterValuesAction = createAction<{ assetFileKinds: AssetFileKind[] }>(
  'REMOVE_MULTIPLE_ASSET_FILE_KIND_FILTER_VALUES',
);
export const removeAssetFileKindFilterValueAction = createAction<{
  assetFileKind: AssetFileKind;
}>('REMOVE_ASSET_FILE_KIND_FILTER_VALUE');
export const clearAssetFileKindFilterValuesAction = createAction('CLEAR_ASSET_FILE_KIND_FILTER_VALUES');

export const clearAllFiltersAction = createAction('CLEAR_ALL_FILTERS');
